import { faEye } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { updateSelectedCertificateId } from '../../../../features/certificateList/certificateListSlice'
import CustomPaginator from '../../../../shared/custom-paginator/CustomPaginator'
import CerificateProductListHeader from '../certificate-product-list-header/CerificateProductListHeader'
import classes from './CertificateProductListGrid.module.scss'
import './CertificateProductListGrid.scss'
import { get } from 'lodash'

import CertificateDetailsV2 from '../../../certificate-list-view/cetificate-list/certificate-details/CertificateDetailsV2'
import ActionsColumn from '../certificate-action-column/ActionsColumn'

// Extra room for the sort icon (~28px), flex gap and cell padding on top of the header text
const HEADER_EXTRA_WIDTH = 72
const MIN_COLUMN_WIDTH = 40

const measureHeaderTextWidth = (() => {
  let context
  return text => {
    if (!context) {
      context = document.createElement('canvas').getContext('2d')
      context.font = '600 16px "Segoe UI", Arial, sans-serif'
    }
    return context.measureText(text ?? '').width
  }
})()

const CertificateProductListGrid = props => {
  const dispatch = useDispatch()
  const clv = useRef(null)
  const [showCertificateDetails, setShowCertificateDetails] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])
  const [expandedRows, setExpandedRows] = useState(null)
  const [dataTableData, setDataTableData] = useState([])
  const [sortField, setSortField] = useState()
  const [sortOrder, setSortOrder] = useState()
  const resizedColumnIds = useRef(new Set())
  const [dropdownOptions, setDropDownOptions] = useState([
    { label: 100, value: 100 },
    { label: 500, value: 500 },
    { label: 1000, value: 1000 },
  ])

  const {
    loading,
    currentPageRef,
    columns,
    subColumns,
    frozenColCount,
    certListData,
    totalCertificateCount,
    totalPageCount,
    pageCount,
    currentPage,
    lazyParams,
    favoriteName,
    favoriteList,
    onClickStatusFilter,
    onChangePage,
    setCertListColumns,
    setCertFrozenCount,
    setShowCertificateProductDetails,
    setIsClickedOutSide,
    onSaveFavorite,
    onEditFavorite,
    onDeleteFavorite,
    onClickFavorites,
    onClearFilters,
  } = props

  const clickableSubBodyTemplate = (rowData, col) => {
    return (
      <div style={{ cursor: 'pointer' }} onClick={() => openCertDetailsWindow(rowData)}>
        {rowData.certificateName}
      </div>
    )
  }

  const tableColumnBody = (rowData, col) => {
    return (
      <div title={get(rowData, col.field)} className={'text-container-ellipsis'}>
        {get(rowData, col.field)}
      </div>
    )
  }

  const processColumnBodyTemplate = col => {
    if (col.id === 'actionsColumnTab') return rowData => <ActionsColumn assetId={rowData.assetId} />

    return tableColumnBody.bind(col)
  }

  const getColumnWidth = fallbackWidth => parseFloat(fallbackWidth) || 0

  const dynamicColumns = columns.map((col, i) => {
    const baseWidth = getColumnWidth(col.width)
    // Start wide enough to show the full header, but let a resized column keep its chosen width
    const headerWidth = Math.ceil(measureHeaderTextWidth(col.value)) + HEADER_EXTRA_WIDTH
    const columnWidth = resizedColumnIds.current.has(col.id)
      ? baseWidth
      : Math.max(baseWidth, headerWidth)

    return (
      <Column
        key={col.id}
        columnKey={col.id}
        field={col.id}
        className={
          col.id === 'modelName'
            ? classes['model-name-column']
            : col.id === 'actionsColumnTab'
              ? classes['actions-column']
              : undefined
        }
        header={() => (
          <div title={col.value} className={classes['column-header-label']}>
            <span>{col.value}</span>
          </div>
        )}
        hidden={!col.isShown}
        alignFrozen={col.id === 'actionsColumnTab' ? 'right' : 'left'}
        frozen={col.isFrozen}
        sortable={col.id !== 'actionsColumnTab'}
        style={{
          flexGrow: 1,
          flexBasis: columnWidth,
          width: columnWidth,
          minWidth: MIN_COLUMN_WIDTH,
        }}
        body={processColumnBodyTemplate(col)}
      />
    )
  })

  const dynamicSubColumns = subColumns.map((col, i) => {
    if (col.value === 'Certificate Number') {
      return (
        <Column
          key={col.id}
          columnKey={col.id}
          field={col.id}
          header={col.value}
          sortable
          style={{ width: col.width }}
          body={clickableSubBodyTemplate}
        />
      )
    } else {
      return (
        <Column
          key={col.id}
          columnKey={col.id}
          field={col.id}
          header={col.value}
          sortable
          style={{ width: col.width }}
        />
      )
    }
  })

  const onColumnResize = e => {
    const { column, element } = e

    resizedColumnIds.current.add(column.props.field)

    const fields = columns.map(f => {
      if (f.id === column.props.field) {
        const defaultWidth = parseFloat(f.defaultWidth ?? f.width) || 0

        return {
          ...f,
          defaultWidth,
          width: Math.max(element.offsetWidth, MIN_COLUMN_WIDTH),
        }
      } else {
        return f
      }
    })

    setCertListColumns(fields)
  }

  const onColReorder = e => {
    if (e.dropIndex === 0 || e.dropIndex === columns.length + 1) {
      clv.current.reset()
    } else {
      let certColumns = columns.map((col, i) => {
        const orderNo = e.columns.findIndex(fi => fi.props.field === col.id)

        return {
          ...col,
          orderNo: orderNo,
        }
      })

      certColumns.sort(function (a, b) {
        return a.orderNo - b.orderNo
      })

      setCertListColumns(certColumns)
    }
  }

  const handleSort = (sortFieldParam, sortOrderParam) => {
    if (!certListData) {
      setDataTableData([])
      return
    }

    setSortField(sortFieldParam)
    setSortOrder(sortOrderParam)

    if (!sortFieldParam || !sortOrderParam) {
      setDataTableData(certListData)
      return
    }

    const isFieldNumerical = () =>
      certListData.every(x => {
        const value = get(x, sortFieldParam)
        return !value || value.length === 0 || !isNaN(value)
      })
    const isFieldDate = () =>
      certListData.every(x => {
        const value = get(x, sortFieldParam)
        return !value || (isNaN(value) && !isNaN(new Date(value).getTime()))
      })

    let sortedData = []

    if (isFieldNumerical()) {
      sortedData = [...certListData].sort((a, b) => {
        const toNum = value => (isNaN(value) ? 0 : parseFloat(value))
        const x = toNum(get(a, sortFieldParam))
        const y = toNum(get(b, sortFieldParam))

        return sortOrderParam === 1 ? x - y : y - x
      })
    } else if (isFieldDate()) {
      sortedData = [...certListData].sort((a, b) => {
        const toDate = value => {
          const date = new Date(value)
          return isNaN(date.getTime()) ? new Date(0) : date
        }
        const x = toDate(get(a, sortFieldParam))
        const y = toDate(get(b, sortFieldParam))

        return sortOrderParam === 1 ? x - y : y - x
      })
    } else {
      sortedData = [...certListData].sort((a, b) => {
        const x = get(a, sortFieldParam) ?? ''
        const y = get(b, sortFieldParam) ?? ''

        return sortOrderParam === 1 ? x.localeCompare(y) : y.localeCompare(x)
      })
    }

    setDataTableData(sortedData)
  }

  const onRowDoubleClick = event => {
    const { data } = event
    dispatch(updateSelectedCertificateId(data.certificateId))
    setShowCertificateProductDetails(true)
    setIsClickedOutSide(false)
  }

  const onRowExpand = event => { }

  const onRowCollapse = event => { }

  const openCertDetailsWindow = data => {
    dispatch(updateSelectedCertificateId(data.certificateId))
    setShowCertificateDetails(true)
  }

  const rowExpansionTemplate = data => {
    return (
      <div className={`${classes['cert-prod-list-sub-row']}`}>
        <DataTable
          scrollable
          scrollHeight="10rem"
          scrollDirection="both"
          value={data.certificates}
          responsiveLayout="scroll"
          dataKey="uniqueCertificateId"
        >
          <Column
            field="uniqueCertificateId"
            header=""
            expander
            style={{ width: '40px' }}
            alignFrozen="left"
            frozen
            reorderable={false}
            body={data => (
              <div style={{ cursor: 'pointer' }} onClick={() => openCertDetailsWindow(data)}>
                <FontAwesomeIcon icon={faEye} />
              </div>
            )}
          />
          {dynamicSubColumns}
        </DataTable>
      </div>
    )
  }

  const expandAll = () => {
    let _expandedRows = {}
    certListData.forEach(p => (_expandedRows[`${p.assetId}`] = true))

    setExpandedRows(_expandedRows)
  }

  const collapseAll = () => {
    setExpandedRows(null)
  }
  const isReadOnly = new URL(window.location.href).searchParams.get('isReadOnly') === 'true'

  // PrimeReact tracks column order internally once dragged, so external reorders (e.g. from Show/Hide) need to be resynced
  useEffect(() => {
    clv.current?.resetColumnOrder()
  }, [columns])

  useEffect(() => {
    handleSort(sortField, sortOrder)
  }, [certListData])

  return (
    <div className="ul-grid__column -x100 -x100-mobile">
      {showCertificateDetails && (
        <CertificateDetailsV2
          isReadOnly={isReadOnly}
          showCertificateDetails={showCertificateDetails}
          setShowCertificateDetails={setShowCertificateDetails}
        />
      )}
      <div className="ul-grid__row -wrap">
        <CerificateProductListHeader
          columns={columns}
          expandAll={expandAll}
          collapseAll={collapseAll}
          expandedRows={expandedRows}
          frozenColCount={frozenColCount}
          favoriteName={favoriteName}
          favoriteList={favoriteList}
          onClickStatusFilter={onClickStatusFilter}
          setCertListColumns={setCertListColumns}
          setCertFrozenCount={setCertFrozenCount}
          onSaveFavorite={onSaveFavorite}
          onEditFavorite={onEditFavorite}
          onDeleteFavorite={onDeleteFavorite}
          onClickFavorites={onClickFavorites}
          onClearFilters={onClearFilters}
        />
      </div>

      <div className="ul-grid__row -wrap">
        <div className="ul-grid__column -x100 -x100-mobile  cert-prod-list-grid-container">
          <DataTable
            ref={clv}
            scrollable
            removableSort
            scrollHeight="80vh"
            scrollDirection="both"
            value={dataTableData}
            size="small"
            lazy
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={e => handleSort(e.sortField, e.sortOrder)}
            onPage
            resizableColumns
            reorderableColumns
            onColumnResizeEnd={onColumnResize}
            onColReorder={onColReorder}
            columnResizeMode="expand"
            responsiveLayout="scroll"
            dataKey="assetId"
            selection={selectedRows}
            onSelectionChange={e => setSelectedRows(e.value)}
            onRowDoubleClick={onRowDoubleClick}
            loading={loading}
            expandedRows={expandedRows}
            onRowToggle={e => setExpandedRows(e.data)}
            onRowExpand={onRowExpand}
            onRowCollapse={onRowCollapse}
            rowExpansionTemplate={rowExpansionTemplate}
          >
            <Column
              field="assetId"
              header=""
              expander
              style={{ width: '40px' }}
              alignFrozen="left"
              frozen
              reorderable={false}
            />
            {dynamicColumns}
          </DataTable>
          <CustomPaginator
            currentPageRef={currentPageRef}
            dropdownOptions={dropdownOptions}
            lazyParams={lazyParams}
            currentPage={currentPage}
            pageCount={pageCount}
            totalPageCount={totalPageCount}
            totalRecordCount={totalCertificateCount}
            onChangePage={onChangePage}
          ></CustomPaginator>
        </div>
      </div>
    </div>
  )
}

export default CertificateProductListGrid
